import { useNavigate } from 'react-router-dom';

const HomePage = () => {
  const navigate = useNavigate();

  const handleSignUp = () => {
    navigate('/auth', { state: { isSignUp: true } });
  };

  return (
    <div className="min-h-screen bg-nova-bg">
      {/* Navigation */}
      <nav className="bg-nova-bg/80 backdrop-blur-sm border-b border-nova-green/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img src="/wordmark.svg" alt="NovaSage" className="h-12" />
            </div>
            <button
              onClick={() => navigate('/auth')}
              className="px-6 py-2 bg-gradient-primary text-white rounded-lg font-semibold hover:opacity-90 transition-all"
            >
              Sign In
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 pb-32 px-4 sm:px-6 lg:px-8">
        {/* Background gradient effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-nova-green/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Tired of feedback that feels{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-nova-green to-teal-400">
                artificial?
              </span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 mb-8 leading-relaxed">
              Imagine getting feedback that truly understands your customers. Feedback that feels like a{' '}
              <span className="text-nova-green font-semibold">real conversation</span>, unearthing insights you never knew existed.
            </p>
            <p className="text-2xl sm:text-3xl font-bold text-white mb-12">
              Welcome to NovaSage.
            </p>

            {/* No Credit Card Badge */}
            <div className="mb-8 inline-flex items-center gap-2 bg-nova-green/10 border border-nova-green/30 px-6 py-3 rounded-full">
              <svg className="w-6 h-6 text-nova-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-nova-green font-bold text-lg">Instant access. No card. No hassle.</span>
            </div>

            {/* CTA Button */}
            <div className="mb-8">
              <button
                onClick={handleSignUp}
                className="px-12 py-4 bg-gradient-primary text-white rounded-lg font-bold text-xl hover:opacity-90 transition-all transform hover:scale-105 shadow-lg"
              >
                Sign Up Now
              </button>
            </div>

            <p className="text-gray-400 text-sm">
              Start collecting profound feedback in minutes.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-nova-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Why NovaSage?
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Stop guessing and start understanding with AI-powered conversational feedback
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Feature 1 */}
            <div className="bg-gradient-card rounded-2xl p-8 border border-nova-green/20 hover:border-nova-green/40 transition-all">
              <div className="w-16 h-16 bg-gradient-primary rounded-xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Rich Conversations, Actionable Insights</h3>
              <p className="text-gray-300 leading-relaxed">
                Gone are the days of rigid, fixed-form surveys. We believe in the power of rich, human conversations.
                Our platform transforms basic responses into deep, actionable insights – because{' '}
                <span className="text-nova-green font-semibold">real people deserve real dialogue</span>.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-gradient-card rounded-2xl p-8 border border-nova-green/20 hover:border-nova-green/40 transition-all">
              <div className="w-16 h-16 bg-gradient-primary rounded-xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">AI-First, Human Intelligence</h3>
              <p className="text-gray-300 leading-relaxed">
                We're an AI-first company, but our true North Star is 'Human Intelligence.' We've harnessed cutting-edge AI
                to amplify the most valuable resource you have: the authentic voice of your customers. It's about getting to the{' '}
                <span className="text-nova-green font-semibold">'why' behind the 'what'</span>, efficiently and effectively.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-gradient-card rounded-2xl p-8 border border-nova-green/20 hover:border-nova-green/40 transition-all">
              <div className="w-16 h-16 bg-gradient-primary rounded-xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Super Simple to Use</h3>
              <p className="text-gray-300 leading-relaxed">
                Getting started couldn't be simpler. Create your NovaSage account in moments. Chat directly with our
                proprietary AI agent to define your feedback goals, and within minutes, you'll have a{' '}
                <span className="text-nova-green font-semibold">unique QR code ready to share</span>.
                It's that easy to start collecting profound feedback.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-gradient-card rounded-2xl p-8 border border-nova-green/20 hover:border-nova-green/40 transition-all">
              <div className="w-16 h-16 bg-gradient-primary rounded-xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Revolutionary AI Conversationalist</h3>
              <p className="text-gray-300 leading-relaxed">
                Our AI feedback collection agent isn't just a chatbot; it's a revolutionary conversationalist.
                It revolutionizes feedback by engaging naturally, guiding participants to provide incredibly rich,
                actionable insights. It{' '}
                <span className="text-nova-green font-semibold">adapts, probes, and ensures you get the full picture</span> – every single time.
              </p>
            </div>

            {/* Feature 5 - Multi-Language Support */}
            <div className="bg-gradient-card rounded-2xl p-8 border border-nova-green/20 hover:border-nova-green/40 transition-all md:col-span-2">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="w-16 h-16 bg-gradient-primary rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-4">Global Reach, No Barriers</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Connect with your audience in their native language – no extra setup required. Our AI automatically{' '}
                    <span className="text-nova-green font-semibold">detects and responds in the language your respondents prefer</span>,
                    whether it's English, Spanish, Mandarin, Arabic, or dozens of other languages. Break down language barriers
                    and gather authentic feedback from diverse, global audiences effortlessly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-nova-secondary/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-300">
              Get started in three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-primary rounded-full flex items-center justify-center text-3xl font-bold text-white mx-auto mb-6">
                1
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Create Account</h3>
              <p className="text-gray-300">
                Sign up in seconds and get instant access to your NovaSage dashboard
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-primary rounded-full flex items-center justify-center text-3xl font-bold text-white mx-auto mb-6">
                2
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Chat with AI</h3>
              <p className="text-gray-300">
                Tell our AI agent your feedback goals and it will create a custom survey
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-primary rounded-full flex items-center justify-center text-3xl font-bold text-white mx-auto mb-6">
                3
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Share & Collect</h3>
              <p className="text-gray-300">
                Get your unique QR code and start collecting rich, conversational feedback
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Ready to unlock the true power of{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-nova-green to-teal-400">
              Human Intelligence?
            </span>
          </h2>
          <p className="text-xl text-gray-300 mb-12">
            Stop guessing and start understanding. Elevate your feedback, empower your decisions,
            and connect with your audience on a whole new level.
          </p>

          <div className="mb-8 inline-flex items-center gap-2 bg-nova-green/10 border border-nova-green/30 px-6 py-3 rounded-full">
            <svg className="w-6 h-6 text-nova-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-nova-green font-bold text-lg">Instant access. No card. No hassle.</span>
          </div>

          <div className="mb-8">
            <button
              onClick={handleSignUp}
              className="px-12 py-4 bg-gradient-primary text-white rounded-lg font-bold text-xl hover:opacity-90 transition-all transform hover:scale-105 shadow-lg"
            >
              Sign Up Now
            </button>
          </div>

          <p className="text-gray-400">
            Join the future of feedback. Create your free NovaSage account today.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-nova-green/20 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <img src="/wordmark.svg" alt="NovaSage" className="h-10" />
            </div>
            <p className="text-gray-400 text-center md:text-right">
              Real Conversations. Real Feedback. Human Intelligence.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
